// MyInfo.js
import React, { useEffect, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import { Button, IconButton } from '@mui/material';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import MyInfoInput from './MyInfoInput';
import ChangeMyPassword from './ChangeMyPassword';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import ResponseAlert from '../message/ResponseAlert';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { GET_MY_INFO_N_ALARMS, CHANGE_USER_INFO_PASSWORD, UserInfoDictionary, EDIT_USER_INFO_MINE } from './UserInfoGql';
import { AppWord, AppObject, AppNumber, AppPalette } from '../../model/AppConst';
import ValueUtil from '../../model/ValueUtil';
import { userInfoRepo, NoUser, logRepo, mmlog } from '../../model/CvoModel';
import Util from '../../model/Util';

const dcPad = AppNumber.dataContainerPadding;
const UserInfoContainer = styled(Box)({ position:"absolute", top:dcPad, bottom:dcPad, left:dcPad, right:dcPad, display:"flex" });

const WorkBox = styled(Box)({
    flexGrow:1, display:'flex', flexDirection:'column', textAlign:'center', alignItems:'center',
    minWidth: 480,
});

const InfoWrapper = styled(Box)({
    display: 'inline-block', textAlign:'left', // backgroundColor:'yellow', // flexGrow:0,
    border: AppPalette.BorderCCC, borderRadius:10, overflow:'auto',
});
const InfoHeader = styled(Box)({
    backgroundColor: AppPalette.HeaderBackground, padding:10, fontWeight:'bold', color:'white'
});
const InfoBody = styled(Box)({
    padding:20,
});
const InfoColumn = styled(Box)({
    display: 'inline-block', verticalAlign: 'top', paddingLeft:50,
    '&:first-of-type': {paddingLeft:0}
});
const InfoLine = styled(Box)({marginBottom:15});
const InfoLabel = styled(Box)({fontSize:'0.85em', color:'#333', marginBottom:5});
const InfoValue = styled(Box)({fontSize:'1.1em', color:'#003'});

const LogLine = styled(Box)({display:'flex', borderBottom:'1px solid #ccc', padding:3});
const LogTime = styled(Box)({width:150, color:'#666', fontSize:'0.8em'});
const LogMessage = styled(Box)({flexGrow:1, fontSize:'0.9em'});


const ErrorTitle =UserInfoDictionary.errorTitle;
const InfoDict = UserInfoDictionary.inputMap;


export default function MyInfo() {
    const sessionInfo = useReactiveVar(userInfoRepo);
    const [myInfo, setMyInfo] = useState({});
    const [alarmList, setAlarmList] = useState([]);
    const [openEditor, setOpenEditor] = useState(false);
    const [openPasswordSetter, setOpenPasswordSetter] = useState(false);
    const [responseAlert, setResponseAlert] = useState(null);
    const memLog = useReactiveVar(logRepo);

    // ##### Call GraphQL to get List #####
    const [getMyUserInfo, responseItemToEdit] = useLazyQuery(GET_MY_INFO_N_ALARMS, {
		...AppObject.NoCachedFetch,
		onCompleted: (data, option) => {onCompleteGetItem(data, option)},
		onError: (error) => {setResponseAlert({open:true, error: error, title: ErrorTitle.Get})}
	});
    
    // ##### GraphQL Mutation.  ###
    const [editUserInfo, responseEdit] = useMutation( EDIT_USER_INFO_MINE, {
		onCompleted: (data, option) => onCompleteEdit(data, option),
		onError: (error) => {setResponseAlert({open:true, error: error, title: ErrorTitle.Edit})}
	} );
    const [changePassword, resChangePassword] = useMutation( CHANGE_USER_INFO_PASSWORD, {
		onCompleted: (data, option) => onCompleteChangePassword(data, option),
		onError: (error) => {setResponseAlert({open:true, error: error, title: ErrorTitle.ChPwd})}
	} );

    useEffect(() => {
        getMyUserInfo({variables:{userId: sessionInfo.user.userId}});
    }, [getMyUserInfo, sessionInfo]);

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteEdit = (data, clientOption) => {
        if(data.userInfoEditMine.ok) {
            setOpenEditor(false);
            getMyUserInfo({variables:{userId: sessionInfo.user.userId}});
        }
        else setResponseAlert({open:true, resultData: data.userInfoEditMine, title: ErrorTitle.Edit});
    };

    const onCompleteChangePassword = (data, option) => {
        if(data.userInfoChangePassword.ok) {
            setOpenPasswordSetter(false);
            Util.bubbleSnack("암호가 변경되었습니다. 새 암호를 잊지 마세요~!");
        }
        else setResponseAlert({open:true, resultData: data.userInfoChangePassword, title: ErrorTitle.ChPwd});
    };

    const onCompleteGetItem = (data, option) => {
        if(data.userInfoItem) {
            setMyInfo(data.userInfoItem);
        }
        if(data.userGetAlarmList) setAlarmList(data.userGetAlarmList.filter((alarm)=>Boolean(alarm.timeFrom)));
    };

    // +++++++ UI callbacks ++++++++
    const onClickEdit = () => {
        if(responseEdit) responseEdit.reset();
        setOpenEditor(true);
    };

    const onClickChangePassword = () => {
        setOpenPasswordSetter(true);
    };

    // Handler - Submit for mutation fired by UserInfoInput component.
    const onClickMutate = (item) => {
        const userInfo = ValueUtil.refineToSubmit(item);
        // userInfo.classNo = sessionInfo.user.classNo;
        const param = {variables: {userInfo: userInfo}};
        editUserInfo(param);
    };

    const onClickMutatePassword = (data) => {
        changePassword({variables: {userInfo:data}});
    };

    if(ValueUtil.hasAnyAuthError(
        responseEdit, responseItemToEdit, resChangePassword
    )) userInfoRepo(NoUser);

    const renderEditor = () => {
        return(
            <MyInfoInput
                open={openEditor}
                isEdit={true}
                item={myInfo}
                responseSaving={responseEdit}
                onClickMutate={onClickMutate}
                onClose={()=>{setOpenEditor(false);}}
                />
        );
    };

    const renderLogLine = (log, idx) => {
        const timeStr = (new Date(log.time)).toLocaleString();
        return (
            <LogLine key={idx}>
                <LogTime>{timeStr}</LogTime>
                <LogMessage>{log.message}</LogMessage>
            </LogLine>
        );
    };


    return (
        <UserInfoContainer>
            <WorkBox>
                <InfoWrapper>
                    <InfoHeader>
                        내 사용자 정보
                    </InfoHeader>
                    <InfoBody>
                        <Box>
                            <InfoColumn>
                                <InfoLine>
                                    <InfoLabel>{InfoDict.userId.label}</InfoLabel>
                                    <InfoValue>{myInfo.userId}</InfoValue>
                                </InfoLine>
                                <InfoLine>
                                    <InfoLabel>{InfoDict.userName.label}</InfoLabel>
                                    <InfoValue>{myInfo.userName}</InfoValue>
                                </InfoLine>
                                <InfoLine>
                                    <InfoLabel>{InfoDict.classNo.label}</InfoLabel>
                                    <InfoValue>{myInfo.className}</InfoValue>
                                </InfoLine>
                                <InfoLine>
                                    <InfoLabel>소속</InfoLabel>
                                    <InfoValue>{myInfo.custName}</InfoValue>
                                </InfoLine>
                                <InfoLine>
                                    <InfoLabel>{InfoDict.telno.label}</InfoLabel>
                                    <InfoValue>{myInfo.telno || '없음'}</InfoValue>
                                </InfoLine>
                                <InfoLine>
                                    <InfoLabel>{InfoDict.mobile.label}</InfoLabel>
                                    <InfoValue>{myInfo.mobile || '없음'}</InfoValue>
                                </InfoLine>
                                <InfoLine>
                                    <InfoLabel>{InfoDict.email.label}</InfoLabel>
                                    <InfoValue>{myInfo.email || '없음'}</InfoValue>
                                </InfoLine>
                            </InfoColumn>
                            <InfoColumn>
                                {
                                    alarmList.length===0
                                    ?
                                    <InfoLine>
                                        <InfoLabel>알람 수신 설정</InfoLabel>
                                        <InfoValue>수신하는 알람 없음</InfoValue>
                                    </InfoLine>
                                    :
                                    alarmList.map((alarm,idx) => {
                                        return(
                                            <InfoLine key={idx}>
                                                <InfoLabel>{alarm.alarmType} 알람 수신 설정</InfoLabel>
                                                <InfoValue>
                                                    <NotificationsActiveIcon fontSize={AppWord.SMALL} color={AppPalette.WarnColor}
                                                        style={{marginRight:10, verticalAlign:'bottom'}}
                                                    />
                                                    {alarm.timeFrom.substr(0,2)} ~ {alarm.timeTill.substr(0,2)}시 ({alarm.alarmInterval}분 간격)</InfoValue>
                                            </InfoLine>
                                        );
                                    })
                                }
                            </InfoColumn>
                        </Box>
                        <Box>
                            <Button onClick={onClickEdit} variant={AppPalette.VariantContained}>
                                <NoteAltIcon fontSize={AppWord.SMALL}/> 수정
                            </Button>
                            <Button onClick={onClickChangePassword} variant={AppPalette.VariantContained}
                                style={{marginLeft:10}}
                            >
                                <VpnKeyIcon fontSize={AppWord.SMALL}/> 암호변경
                            </Button>
                        </Box>
                    </InfoBody>
                </InfoWrapper>
            </WorkBox>
            {
                (sessionInfo.isSystemAdmin() || sessionInfo.user.userId==='etrace') &&
                <Box
                    style={{
                        border: '1px solid red', borderRadius:10, padding:10,
                        display: 'flex', flexDirection:'column',
                        minWidth: 400, flexGrow:1, marginLeft:10, // maxWidth: 600,
                    }}
                >
                    <Box style={{
                        fontWeight:'bold', backgroundColor: '#eee', padding:5, marginBottom:10,
                        borderRadius:10, textAlign:'center',
                        display:'flex', alignItems:'center', justifyContent:'center',
                    }}>
                        <span>시스템 관리자와 시연용 ID만 볼 수 있는 영역입니다.</span>
                        <IconButton
                            title="로그 지우기"
                            onClick={()=>{
                                logRepo([]);
                                mmlog('로그가 지워졌습니다.');
                            }}
                            size="small"
                            style={{marginLeft:10}}
                        >
                            <CleaningServicesIcon />
                        </IconButton>
                    </Box>
                    <Box style={{flexGrow:1, overflow:'auto', fontSize:'0.8em'}}>
                        {
                            memLog.length===0
                            ?
                            <Box>로그가 없습니다.</Box>
                            :
                            memLog.map( (log, idx) => {
                                return renderLogLine(log, idx);
                            } )
                        }
                    </Box>
                </Box>
            }
            {renderEditor()}
            <ChangeMyPassword
                userInfo={myInfo}
                open={openPasswordSetter}
                onClickMutate={onClickMutatePassword}
                onClose={()=>{setOpenPasswordSetter(false)}}
                responseSaving={resChangePassword}
            />
            <ResponseAlert open={responseAlert ? responseAlert.open : false}
                alertData={responseAlert}
                onClose={() => {setResponseAlert(null)}}/>
        </UserInfoContainer>
    )
}
/*


*/